import { create } from 'zustand';

export interface AuthUser {
  id: number;
  nome: string;
  email: string;
  cargo: string;
  unidade: string | null;
}

interface AuthStore {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  bootstrapped: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  /**
   * Tenta inferir o usuário do cookie .grupomultiluz.com.br
   * (caso típico dentro do iframe Pipefy). Sem efeito se já tem sessão.
   */
  bootstrapFromCookie: () => Promise<boolean>;
}

const STORAGE_TOKEN = 'engenharia_token';
const STORAGE_USER = 'engenharia_user';

function loadFromSession(): { token: string | null; user: AuthUser | null } {
  try {
    const token = sessionStorage.getItem(STORAGE_TOKEN);
    const userRaw = sessionStorage.getItem(STORAGE_USER);
    const user = userRaw ? (JSON.parse(userRaw) as AuthUser) : null;
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

const initial = loadFromSession();

export const useAuthStore = create<AuthStore>((set, get) => ({
  token: initial.token,
  user: initial.user,
  isAuthenticated: !!initial.token && !!initial.user,
  bootstrapped: false,

  login: (token, user) => {
    sessionStorage.setItem(STORAGE_TOKEN, token);
    sessionStorage.setItem(STORAGE_USER, JSON.stringify(user));
    set({ token, user, isAuthenticated: true, bootstrapped: true });
  },

  logout: () => {
    sessionStorage.removeItem(STORAGE_TOKEN);
    sessionStorage.removeItem(STORAGE_USER);
    set({ token: null, user: null, isAuthenticated: false });
  },

  bootstrapFromCookie: async () => {
    if (get().isAuthenticated) {
      set({ bootstrapped: true });
      return true;
    }
    try {
      // Import dinâmico pra evitar ciclo com client.ts (que importa o store).
      const { default: api } = await import('../api/client');
      const r = await api.get('/auth/whoami', { validateStatus: () => true });
      if (r.status === 200 && r.data?.user) {
        const u = r.data.user as AuthUser;
        // Sem token JS (a fonte é cookie HttpOnly), mas armazena user pra UI.
        sessionStorage.setItem(STORAGE_USER, JSON.stringify(u));
        set({ user: u, isAuthenticated: true, bootstrapped: true });
        return true;
      }
    } catch (e) {
      console.warn('[auth] whoami falhou', e);
    }
    set({ bootstrapped: true });
    return false;
  },
}));

export function getAuthToken(): string | null {
  return sessionStorage.getItem(STORAGE_TOKEN);
}
